import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { FaEdit, FaTrash, FaPlus } from 'react-icons/fa';
import ExpenseModal from './ExpenseModal';
import { useParams, useNavigate } from 'react-router-dom';
import { Link } from 'react-router-dom';
import { useSearchParams } from 'react-router-dom';
import ReactPaginate from 'react-paginate';
import { useAuth } from './../../../context/AuthContext';
import withPermission from './../../../utils/withPermission';
import Swal from 'sweetalert2';
import withReactContent from "sweetalert2-react-content";

const ExpenseList = ({ projectId, projectDetails }) => {
    const [expense, setExpense] = useState([]);
    const [lockedPeriods, setLockedPeriods] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [selectedExpenseId, setSelectedExpenseId] = useState(null);
    const [selectedExpenseData, setSelectedExpenseData] = useState(null);
    const [errors, setErrors] = useState({});
    const didFetchRef = useRef(false);
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [currentPage, setCurrentPage] = useState(1);
    const [pageCount, setPageCount] = useState(1);
    const { id } = useParams();
    const { roles, permissions } = useAuth();
    const WithDeletePermission = withPermission(({ children }) => children, ['expense-delete']);
    const WithEditPermission = withPermission(({ children }) => children, ['expense-edit']);
    const WithCreatePermission = withPermission(({ children }) => children, ['expense-create']);
    const [searchQuery, setSearchQuery] = useState('');
    const debounceTimeoutRef = useRef(null);
    const [expenseTypes, setExpenseTypes] = useState([]);
    const [users, setUsers] = useState([]);
    const [baseCurrency, setBaseCurrency] = useState('AED'); // Default is AED
    const [totalExpenseAmount, setTotalExpenseAmount] = useState(0);
    const MySwal = withReactContent(Swal);
    const [isEditMode, setIsEditMode] = useState(false);

    useEffect(() => {
        if (debounceTimeoutRef.current) {
            clearTimeout(debounceTimeoutRef.current);
        }

        debounceTimeoutRef.current = setTimeout(() => {
            fetchExpense(1, id, searchQuery);
        }, searchQuery ? 1500 : 0);

        return () => {
            if (debounceTimeoutRef.current) {
                clearTimeout(debounceTimeoutRef.current);
            }
        };
    }, [searchQuery]);
    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);

    };

    const fetchExpense = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            try {
                const lpResponse = await axios.get(`${backendUrl}/api/accounting/locked-periods`, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    },
                });
                setLockedPeriods(lpResponse.data.locked_periods || []);
            } catch (err) {
                console.error("Failed to fetch locked periods", err);
            }

            const response = await axios.get(`${backendUrl}/api/expense`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,

                },
                params: {
                    page,
                    project_id: projectId,
                    search: searchQuery,
                },
                skipLoader: true,

            });

            const expenseData = response.data.data?.data || [];

            // Extract pagination info correctly
            const totalPages = response.data.data?.last_page || 1;
            const currentPage = response.data.data?.current_page || 1;
            const totalAmount = response.data.total_expense_amount || 0;
            setExpense(expenseData);
            setPageCount(totalPages);
            setCurrentPage(currentPage);
            setTotalExpenseAmount(totalAmount);
        } catch (error) {
            console.error('Error fetching expense:', error);
        }
    };
    const fetchExpenseTypes = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;

            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');

            const token = userInfo?.token;
            const response = await axios.get(`${backendUrl}/api/expense-type`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,


            });
            const sortedExpenseTypes = (response.data.expense || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setExpenseTypes(sortedExpenseTypes);



        } catch (error) {
            console.error('Error fetching expenseTypes:', error);
        }
    };
    const fetchUsers = async (page = 1) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const response = await axios.get(`${backendUrl}/api/users`, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,

            });
            const sortedUsers = (response.data.users || []).sort((a, b) => (a.name || '').localeCompare(b.name || ''));
            setUsers(sortedUsers);



        } catch (error) {
            console.error('Error fetching users:', error);
        }
    };
    const fetchCompanyDetails = async () => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info')); // Get user token
            const origin = localStorage.getItem('user-origin'); // Get tenant origin
            const token = userInfo?.token;

            const response = await axios.get(`${backendUrl}/api/settings`, {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Tenant-Origin': origin,
                },
                skipLoader: true,

            });

            if (response.data.setting && response.data?.setting?.currency) {
                setBaseCurrency(response.data?.setting?.currency); // Set currency from API
            }
        } catch (error) {
            console.error('Error fetching company details:', error);
        }
    };
    useEffect(() => {
        fetchCompanyDetails();
    }, []);


    const addExpense = async (newExpense) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            const newExpenseAmount = parseFloat(newExpense.get("amount")); // Get value from FormData
            const totalWithNewExpense = totalExpenseAmount + newExpenseAmount;
            const expenditureLimit = parseFloat(projectDetails.expenditure_amount);

            // Check if new total exceeds the limit
            if (totalWithNewExpense > expenditureLimit) {
                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'warning',
                    text: `Budget Exceeded! Adding this expense will exceed  ${projectDetails.expenditure_amount}.`,
                    showConfirmButton: false,
                    timer: 4000,
                    timerProgressBar: false
                });
                return;
            }

            // Ensure `FormData` is correctly formatted
            newExpense.append("project_id", projectId); // Attach the correct project ID

            // Use `multipart/form-data` headers
            const response = await axios.post(`${backendUrl}/api/expense`, newExpense, {
                headers: {
                    "Authorization": `Bearer ${token}`,
                    "Content-Type": "multipart/form-data",
                    'Tenant-Origin': origin,
                },
            });

            const createdExpense = response.data?.data;
            if (createdExpense) {
                await runDoubleEntry(createdExpense);
                setExpense((prevExpense) =>
                    Array.isArray(prevExpense) ? [...prevExpense, createdExpense] : [createdExpense]
                );
                await fetchExpense();
            } else {
                console.error("Creation failed, no data returned.");
            }
            handleCloseModal();
        } catch (error) {
            if (error.response?.data?.message) {
                return error.response.data.message; // Return validation errors
            } else {
                console.error("Error adding expense:", error);
            }
        }
    };

    const editExpense = async (expenseId, updatedData) => {
        try {
            const backendUrl = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;
            const webzashBaseUrl = process.env.REACT_APP_WEBZASH_URL || 'http://localhost/webzash-integration';
            const APIkey = process.env.REACT_APP_API_KEY || '';

            const config = {
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'multipart/form-data',
                    'Tenant-Origin': origin,
                },
            };

            // 1. First update the expense in the main system
            const response = await axios.post(
                `${backendUrl}/api/expense/${expenseId}?_method=PUT`,
                updatedData,
                config
            );

            const updatedExpense = response.data?.data;
            if (!updatedExpense) {
                throw new Error('Failed to update expense');
            }

            // 2. Immediately update local state with the basic updated data
            setExpense(prevExpenses =>
                prevExpenses.map(expense =>
                    expense.id === expenseId ? { ...expense, ...updatedExpense } : expense
                )
            );

            // 3. Handle Webzash journal entry
            const webzashHeaders = {
                'x-api-key': APIkey,
                'Accept': 'application/json',
                'Content-Type': 'application/json',
            };

            // Delete old journal entry if exists
            if (updatedExpense.journal_id) {
                try {
                    await axios.delete(
                        `${webzashBaseUrl}/api/entries/delete/journal/${updatedExpense.journal_id}`,
                        { headers: webzashHeaders }
                    );
                } catch (deleteError) {
                    console.warn('Failed to delete old journal entry:', deleteError);
                }
            }

            // Create new journal entry
            try {
                const journalEntry = await runDoubleEntry(updatedExpense);

                if (journalEntry && journalEntry.id) {
                    setExpense(prevExpenses =>
                        prevExpenses.map(expense =>
                            expense.id === expenseId
                                ? { ...expense, journal_id: journalEntry.id }
                                : expense
                        )
                    );
                } else {
                    console.warn('No journal entry returned from runDoubleEntry');
                }

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'success',
                    title: 'Expense updated successfully',
                    showConfirmButton: false,
                    timer: 3000
                });
            } catch (webzashError) {
                console.error('Webzash entry creation failed:', webzashError);

                Swal.fire({
                    toast: true,
                    position: 'top-end',
                    icon: 'warning',
                    title: 'Expense updated but accounting sync failed',
                    showConfirmButton: false,
                    timer: 4000
                });
            }

            handleCloseModal();
            return null;
        } catch (error) {
            console.error('Error updating expense:', error);

            let errorMessage = 'Failed to update expense';
            if (error.response?.data?.message) {
                errorMessage = error.response.data.message;
            } else if (error.message) {
                errorMessage = error.message;
            }

            Swal.fire({
                toast: true,
                position: 'top-end',
                icon: 'error',
                title: errorMessage,
                showConfirmButton: false,
                timer: 3000
            });

            return error.response?.data?.errors || { general: errorMessage };
        }
    };

    const deleteExpense = async (id) => {
        Swal.fire({
            title: "Are you sure?",
            text: "You won't be able to revert this!",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Yes, delete it!"
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    const backendUrl = process.env.REACT_APP_BACKEND_URL;

                    const userInfo = JSON.parse(localStorage.getItem('user-info'));
                    const origin = localStorage.getItem('user-origin');
                    const token = userInfo?.token;
                    const webzashBaseUrl = process.env.REACT_APP_WEBZASH_URL || 'http://localhost/webzash-integration';
                    const APIkey = process.env.REACT_APP_API_KEY || '';
                    const expenseRes = await axios.get(`${backendUrl}/api/expense/${id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });
                    const journalId = expenseRes?.data?.journal_id;

                    // Step 2: Delete journal if exists
                    if (journalId) {
                        try {
                            await axios.delete(`${webzashBaseUrl}/api/entries/delete/journal/${journalId}`, {
                                headers: {
                                    'x-api-key': APIkey,
                                }
                            });
                        } catch (err) {
                            console.warn("⚠️ Journal delete failed but continuing with expense delete.");
                        }
                    }

                    await axios.delete(` ${backendUrl}/api/expense/${id}`, {
                        headers: {
                            'Authorization': `Bearer ${token}`,
                            'Tenant-Origin': origin,
                        },
                    });

                    setExpense(expense.filter(expense => expense.id !== id));
                    Swal.fire(
                        "Deleted!",
                        "The expense has been deleted.",
                        "success"
                    );
                } catch (error) {
                    console.error('Error deleting expense:', error);
                    Swal.fire(
                        "Error!",
                        "Failed to delete expense. Please try again.",
                        "error"
                    );
                }
            }
        });
    };

    const generateUniqueEntryNumber = () => {
        const timestamp = Date.now(); // e.g., 1717512958000
        const random = Math.floor(100 + Math.random() * 900); // 3-digit random
        return parseInt(`${timestamp}${random}`.slice(-10)); // limit to 10 digits
    };
    const showToast = (message, type = "error") => {
        MySwal.fire({
            toast: true,
            position: "top-end",
            icon: type,
            title: message,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };

    const showToast1 = (message, type = "show") => {
        MySwal.fire({
            toast: true,
            position: "top-end",
            icon: type,
            title: message,
            showConfirmButton: false,
            timer: 3000,
            timerProgressBar: true,
        });
    };
    const runDoubleEntry = async (expenseData) => {
        const API = process.env.REACT_APP_WEBZASH_URL || 'http://localhost/webzash-integration';
        const backendUrl = `${API}/api`;
        console.log("url", backendUrl);

        const token = localStorage.getItem("token");
        const headers = {
            'x-api-key': process.env.REACT_APP_API_KEY,
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        };

        try {
            const groupRes = await axios.get(`${backendUrl}/groups/index`, { headers });
            const allGroups = groupRes.data;
            if (!Array.isArray(allGroups)) {
                showToast("❌ Failed to load groups");
                return;
            }

            const ledgerRes = await axios.get(`${backendUrl}/ledgers/index`, { headers });
            const allLedgers = ledgerRes.data?.data;
            if (!Array.isArray(allLedgers)) {
                showToast("❌ Failed to load ledgers");
                return;
            }

            await createDoubleEntryForExpense(expenseData, allGroups, allLedgers, backendUrl, headers);
        } catch (error) {
            console.error("❌ Error in double entry logic:", error);
        }
    };

    const createDoubleEntryForExpense = async (expense, allGroups, allLedgers, backendUrl, headers) => {
        const amount = parseFloat(expense.amount);
        const expenseType = expense.expense_type;
        const adjustmentAmount = expense.adjustment_amount ? parseFloat(expense.adjustment_amount) : 0;

        const user = expense.user;
        const vendor = expense.vendor;

        const isRcmEligible =
            expense.rcm_eligible == 1 || expense.rcm_eligible === true || expense.rcm_eligible === '1' || expense.rcm_eligible === 'true' ||
            expense.vendor?.rcm_eligible == 1 || expense.vendor?.rcm_eligible === true || expense.vendor?.rcm_eligible === '1' || expense.vendor?.rcm_eligible === 'true';

        console.log(`🔍 RCM Eligibility Check:`, {
            isRcmEligible,
            expense_rcm: expense.rcm_eligible,
            vendor_rcm: expense.vendor?.rcm_eligible,
            vendor_name: expense.vendor?.name,
            tax_type: expense.tax_type
        });

        if (!expenseType || (!vendor && !user)) {
            showToast("❌ Missing expense type or vendor/user.");
            return;
        }

        // ========== STEP 1: Ensure expense category group exists ==========
        // STEP 1: Ensure expense category group exists
        const categoryName = expenseType.category?.name;
        const categoryGroupId = expenseType.category?.group_id; // 👈 Must be passed from backend
        let expenseGroup = allGroups.find(g => g.Group?.name === categoryName)?.Group;

        if (!expenseGroup) {
            if (!categoryGroupId) {
                showToast("❌ Category is missing a group_id. Cannot create group.");
                return;
            }

            // Check if the parent group exists in allGroups
            const parentGroup = allGroups.find(g => g.Group?.id === categoryGroupId)?.Group;

            if (!parentGroup) {
                showToast("❌ Parent group for this category not found.");
                return;
            }

            const groupPayload = {
                Group: {
                    name: categoryName,
                    parent_id: categoryGroupId, // ✅ Use the correct parent group (Direct/Indirect/Purchase)
                    affect_gross: 1,
                    code: null,
                }
            };

            const res = await axios.post(`${backendUrl}/groups/add`, groupPayload, { headers });
            const groupId = res.data?.id;

            if (!groupId) {
                showToast("❌ Failed to create group for this category.");
                return;
            }

            expenseGroup = { id: parseInt(groupId), name: categoryName };
        }

        let adjustmentLedgerId = null;
        if (adjustmentAmount !== 0) {
            const adjustmentLedgerName = "Adjustment Charges";
            const existingAdjustmentLedger = allLedgers.find(l => l.Ledger?.name === adjustmentLedgerName);

            if (existingAdjustmentLedger) {
                adjustmentLedgerId = existingAdjustmentLedger.Ledger.id;
            } else {
                let bankFinGroup = allGroups.find(g => {
                    const normalized = g.Group?.name?.toLowerCase().replace(/&/g, 'and').replace(/\s+/g, ' ').trim();
                    return normalized === "banking and financial charges";
                })?.Group;

                if (!bankFinGroup) {
                    const indirectGroup = allGroups.find(g => g.Group?.name === "Indirect Expenses")?.Group;
                    if (!indirectGroup) {
                        showToast("❌ 'Indirect Expenses' group not found.");
                        return;
                    }

                    const createBankingGroup = await axios.post(`${backendUrl}/groups/add`, {
                        Group: {
                            name: "Banking and Financial Charges",
                            parent_id: indirectGroup.id,
                            affect_gross: 1,
                            code: null,
                        }
                    }, { headers });

                    const newGroupId = createBankingGroup.data?.id;
                    if (!newGroupId) {
                        showToast("❌ Failed to create 'Banking and Financial Charges' group.");
                        return;
                    }

                    const refresh = await axios.get(`${backendUrl}/groups/index`, { headers });
                    allGroups = refresh.data;
                    bankFinGroup = allGroups.find(g => g.Group?.id == newGroupId)?.Group;
                }

                const adjustmentLedgerPayload = {
                    Ledger: {
                        name: adjustmentLedgerName,
                        group_id: bankFinGroup.id,
                        op_balance: 0,
                        op_balance_dc: "D",
                        notes: "Expense adjustment amount",
                        code: null,
                        type: 0,
                        reconciliation: 0,
                    }
                };

                const createLedger = await axios.post(`${backendUrl}/ledgers/add`, adjustmentLedgerPayload, { headers });
                adjustmentLedgerId = createLedger.data?.data?.Ledger?.id;
            }
        }

        // ========== STEP 2: Ensure expense ledger exists ==========
        const expenseLedgerName = expenseType.name;
        let debitLedger = allLedgers.find(l => l.Ledger?.name === expenseLedgerName);
        let debitLedgerId = null;

        if (debitLedger) {
            debitLedgerId = debitLedger.Ledger.id;
        } else {
            const ledgerPayload = {
                Ledger: {
                    name: expenseLedgerName,
                    group_id: expenseGroup.id,
                    op_balance: 0,
                    op_balance_dc: "D",
                    notes: "",
                    code: null,
                    type: 0,
                    reconciliation: 0,
                }
            };

            try {
                const res = await axios.post(`${backendUrl}/ledgers/add`, ledgerPayload, { headers });
                debitLedgerId = res.data?.data?.Ledger?.id;
            } catch (err) {
                const refresh = await axios.get(`${backendUrl}/ledgers/index`, { headers });
                const existing = refresh.data?.data?.find(l => l.Ledger?.name === expenseLedgerName);
                debitLedgerId = existing?.Ledger?.id;

                if (!debitLedgerId) {
                    showToast("❌ Expense ledger exists but ID not found.");
                    return;
                }
            }
        }

        // ========== STEP 3: Determine credit ledger ==========
        let creditLedgerId = null;
        let creditLedgerName = "";
        let creditLedgerGroupName = "";

        if (vendor && user) {
            creditLedgerName = user.name;
            creditLedgerGroupName = "Loans (Liabilities)";
        } else if (vendor) {
            creditLedgerName = vendor.name;
            creditLedgerGroupName = "Sundry Creditors (Vendors)";
        } else if (user) {
            creditLedgerName = user.name;
            creditLedgerGroupName = "Loans (Liabilities)";
        }

        // ========== STEP 4: Find or create credit ledger ==========
        let creditGroup = allGroups.find(g => g.Group?.name === creditLedgerGroupName)?.Group;

        if (!creditGroup) {
            const parentLiabilitiesGroup = allGroups.find(g => g.Group?.name === "Liabilities and Owners Equity")?.Group;

            if (!parentLiabilitiesGroup) {
                showToast("'Liabilities and Owners Equity' group not found.");
                return;
            }

            const groupPayload = {
                Group: {
                    name: creditLedgerGroupName,
                    parent_id: parentLiabilitiesGroup.id,
                    affect_gross: 0,
                    code: null
                }
            };

            const groupRes = await axios.post(`${backendUrl}/groups/add`, groupPayload, { headers });
            const newGroupId = groupRes.data?.id;

            if (!newGroupId) {
                showToast(`Failed to create group '${creditLedgerGroupName}'.`);
                return;
            }

            const groupRes2 = await axios.get(`${backendUrl}/groups/index`, { headers });
            allGroups = groupRes2.data;
            creditGroup = allGroups.find(g => g.Group?.id == newGroupId)?.Group;
        }

        let creditLedger = allLedgers.find(l => l.Ledger?.name === creditLedgerName);
        if (creditLedger) {
            creditLedgerId = creditLedger.Ledger.id;
        } else {
            const creditLedgerPayload = {
                Ledger: {
                    name: creditLedgerName,
                    group_id: creditGroup.id,
                    op_balance: 0,
                    op_balance_dc: "C",
                    notes: "",
                    code: null,
                    type: 0,
                    reconciliation: 0,
                }
            };

            try {
                const res = await axios.post(`${backendUrl}/ledgers/add`, creditLedgerPayload, { headers });
                creditLedgerId = res.data?.data?.Ledger?.id;
            } catch (err) {
                const refresh = await axios.get(`${backendUrl}/ledgers/index`, { headers });
                const existing = refresh.data?.data?.find(l => l.Ledger?.name === creditLedgerName);
                creditLedgerId = existing?.Ledger?.id;

                if (!creditLedgerId) {
                    showToast(`Ledger '${creditLedgerName}' exists but ID not found.`);
                    return;
                }
            }
        }

        // ========== STEP 5: Handle vendor-specific cases ==========
        let vendorLedgerId = null;
        if (vendor && user) {
            // Handle vendor ledger creation
            const vendorLedgerName = vendor.name;
            const vendorGroupName = "Sundry Creditors (Vendors)";

            let vendorGroup = allGroups.find(g => g.Group?.name === vendorGroupName)?.Group;
            if (!vendorGroup) {
                const parentLiabilitiesGroup = allGroups.find(g => g.Group?.name === "Liabilities and Owners Equity")?.Group;
                const vendorGroupPayload = {
                    Group: {
                        name: vendorGroupName,
                        parent_id: parentLiabilitiesGroup.id,
                        affect_gross: 0,
                        code: null,
                    }
                };
                const groupRes = await axios.post(`${backendUrl}/groups/add`, vendorGroupPayload, { headers });
                const newGroupId = groupRes.data?.id;
                const refresh = await axios.get(`${backendUrl}/groups/index`, { headers });
                allGroups = refresh.data;
                vendorGroup = allGroups.find(g => g.Group?.id == newGroupId)?.Group;
            }

            let vendorLedger = allLedgers.find(l => l.Ledger?.name === vendorLedgerName);
            if (!vendorLedger) {
                const vendorLedgerPayload = {
                    Ledger: {
                        name: vendorLedgerName,
                        group_id: vendorGroup.id,
                        op_balance: 0,
                        op_balance_dc: "C",
                        notes: "",
                        code: null,
                        type: 0,
                        reconciliation: 0,
                    }
                };
                const res = await axios.post(`${backendUrl}/ledgers/add`, vendorLedgerPayload, { headers });
                vendorLedgerId = res.data?.data?.Ledger?.id;
            } else {
                vendorLedgerId = vendorLedger.Ledger.id;
            }
        }

        // ========== STEP 6: Calculate amounts ==========
        let conversionRate = 1;
        if (vendor && vendor.currency === "USD") {
            conversionRate = 3.67;
        }

        let expenseAmount = 0;
        let vatAmount = 0;

        if (expense.tax_type === 'taxable') {
            const baseAmount = parseFloat(expense.amount_without_tax || 0);
            const taxRate = parseFloat(expense.tax || 5) / 100;
            expenseAmount = parseFloat((baseAmount * conversionRate).toFixed(2));

            // Use saved vat_amount if available, otherwise calculate using tax rate
            const rawVat = expense.vat_amount;
            if (rawVat !== null && rawVat !== undefined && rawVat !== '') {
                vatAmount = parseFloat((parseFloat(rawVat) * conversionRate).toFixed(2));
            } else {
                vatAmount = parseFloat((expenseAmount * taxRate).toFixed(2));
            }
            const convertedAdjustmentAmount = parseFloat((adjustmentAmount * conversionRate).toFixed(2));

        } else {
            const baseAmount = parseFloat(expense.amount || 0);
            expenseAmount = parseFloat((baseAmount * conversionRate).toFixed(2));

            // For RCM, use saved vat_amount if available
            const rawVat = expense.vat_amount;
            if (isRcmEligible) {
                if (rawVat !== null && rawVat !== undefined && rawVat !== '') {
                    vatAmount = parseFloat((parseFloat(rawVat) * conversionRate).toFixed(2));
                } else {
                    vatAmount = parseFloat((expenseAmount * 0.05).toFixed(2));
                }
            } else {
                vatAmount = 0;
            }
        }

        // ========== STEP 7: Handle VAT ledger ==========
        let inputVATLedgerId = null;
        if (vatAmount > 0) {
            let taxAssetGroup = allGroups.find(g => g.Group?.name === "Tax Assets")?.Group;

            if (!taxAssetGroup) {
                const parentAssetsGroup = allGroups.find(g => g.Group?.name === "Assets")?.Group;
                const groupPayload = {
                    Group: {
                        name: "Tax Assets",
                        parent_id: parentAssetsGroup.id,
                        affect_gross: 0,
                        code: null
                    }
                };
                const res = await axios.post(`${backendUrl}/groups/add`, groupPayload, { headers });
                taxAssetGroup = { id: parseInt(res.data?.id), name: "Tax Assets" };
            }

            const inputVATLedgerName = "Input VAT";
            let inputVATLedger = allLedgers.find(l => l.Ledger?.name === inputVATLedgerName);
            if (!inputVATLedger) {
                const vatLedgerPayload = {
                    Ledger: {
                        name: inputVATLedgerName,
                        group_id: taxAssetGroup.id,
                        op_balance: 0,
                        op_balance_dc: "D",
                        notes: "",
                        code: null,
                        type: 0,
                        reconciliation: 0,
                    }
                };
                const res = await axios.post(`${backendUrl}/ledgers/add`, vatLedgerPayload, { headers });
                inputVATLedgerId = res.data?.data?.Ledger?.id;
            } else {
                inputVATLedgerId = inputVATLedger.Ledger.id;
            }
        }

        // ========== STEP 7.2: Handle RCM VAT ledgers ==========
        let rcmInputVATLedgerId = null;
        let rcmOutputVATLedgerId = null;

        if (vatAmount > 0 && isRcmEligible) {
            // 1. RCM Input VAT Ledger (Asset)
            const rcmInputVATLedgerName = "Input VAT – RCM";
            let rcmInputLedger = allLedgers.find(l => l.Ledger?.name === rcmInputVATLedgerName);

            if (!rcmInputLedger) {
                let taxAssetGroup = allGroups.find(g => g.Group?.name === "Tax Assets")?.Group;
                if (!taxAssetGroup) {
                    const parentAssetsGroup = allGroups.find(g => g.Group?.name === "Assets")?.Group;
                    const res = await axios.post(`${backendUrl}/groups/add`, {
                        Group: { name: "Tax Assets", parent_id: parentAssetsGroup.id, affect_gross: 0, code: null }
                    }, { headers });
                    taxAssetGroup = { id: parseInt(res.data?.id), name: "Tax Assets" };
                }

                const res = await axios.post(`${backendUrl}/ledgers/add`, {
                    Ledger: {
                        name: rcmInputVATLedgerName,
                        group_id: taxAssetGroup.id,
                        op_balance: 0,
                        op_balance_dc: "D",
                        notes: "Reverse Charge Mechanism Input VAT",
                        code: null,
                        type: 0,
                        reconciliation: 0,
                    }
                }, { headers });
                rcmInputVATLedgerId = res.data?.data?.Ledger?.id;
            } else {
                rcmInputVATLedgerId = rcmInputLedger.Ledger.id;
            }

            // 2. RCM Output VAT Ledger (Liability)
            const rcmOutputVATLedgerName = "Output VAT – RCM";
            let rcmOutputLedger = allLedgers.find(l => l.Ledger?.name === rcmOutputVATLedgerName);

            if (!rcmOutputLedger) {
                let taxLiabilitiesGroup = allGroups.find(g => g.Group?.name === "Duties and Taxes")?.Group;
                if (!taxLiabilitiesGroup) {
                    const parentLiabilitiesGroup = allGroups.find(g => g.Group?.name === "Liabilities and Owners Equity")?.Group;
                    const res = await axios.post(`${backendUrl}/groups/add`, {
                        Group: { name: "Duties and Taxes", parent_id: parentLiabilitiesGroup.id, affect_gross: 0, code: null }
                    }, { headers });
                    taxLiabilitiesGroup = { id: parseInt(res.data?.id), name: "Duties and Taxes" };
                }

                const res = await axios.post(`${backendUrl}/ledgers/add`, {
                    Ledger: {
                        name: rcmOutputVATLedgerName,
                        group_id: taxLiabilitiesGroup.id,
                        op_balance: 0,
                        op_balance_dc: "C",
                        notes: "Reverse Charge Mechanism Output VAT",
                        code: null,
                        type: 0,
                        reconciliation: 0,
                    }
                }, { headers });
                rcmOutputVATLedgerId = res.data?.data?.Ledger?.id;
            } else {
                rcmOutputVATLedgerId = rcmOutputLedger.Ledger.id;
            }
        }

        // ========== STEP 8: Build entry items ==========
        const entryItems = [];

        // 1. Debit expense ledger
        entryItems.push({
            dc: "D",
            ledger_id: debitLedgerId,
            dr_amount: expenseAmount.toFixed(2),
            cr_amount: null
        });

        // 2. Debit VAT if applicable
        if (vatAmount > 0) {
            if (isRcmEligible) {
                entryItems.push({
                    dc: "D",
                    ledger_id: rcmInputVATLedgerId,
                    dr_amount: vatAmount.toFixed(2),
                    cr_amount: null
                });
                entryItems.push({
                    dc: "C",
                    ledger_id: rcmOutputVATLedgerId,
                    dr_amount: null,
                    cr_amount: vatAmount.toFixed(2)
                });
            } else {
                entryItems.push({
                    dc: "D",
                    ledger_id: inputVATLedgerId,
                    dr_amount: vatAmount.toFixed(2),
                    cr_amount: null
                });
            }
        }
        const convertedAdjustmentAmount = parseFloat((adjustmentAmount * conversionRate).toFixed(2));

        if (convertedAdjustmentAmount !== 0 && adjustmentLedgerId) {
            const isNegative = convertedAdjustmentAmount < 0;
            entryItems.push({
                dc: isNegative ? "C" : "D",
                ledger_id: adjustmentLedgerId,
                dr_amount: isNegative ? null : Math.abs(convertedAdjustmentAmount).toFixed(2),
                cr_amount: isNegative ? Math.abs(convertedAdjustmentAmount).toFixed(2) : null
            });
        }

        const totalCreditAmountCharge = isRcmEligible
            ? (expenseAmount + convertedAdjustmentAmount)
            : (expenseAmount + vatAmount + convertedAdjustmentAmount);

        // 3. Handle vendor/user combinations
        if (vendor && user) {
            // Employee paid the vendor. We credit the Employee (User) and Debit Expense.
            // Vendor ledger is NOT hit as requested.
            entryItems.push({
                dc: "C",
                ledger_id: creditLedgerId,
                dr_amount: null,
                cr_amount: totalCreditAmountCharge.toFixed(2)
            });
        } else {
            // Regular case - single credit entry
            entryItems.push({
                dc: "C",
                ledger_id: creditLedgerId,
                dr_amount: null,
                cr_amount: totalCreditAmountCharge.toFixed(2)
            });
        }
        if (!creditLedgerId) {
            showToast("❌ Credit ledger not found. Cannot proceed.");
            return;
        }

        // ========== STEP 9: Create final payload ==========
        const entryPayload = {
            Entry: {
                tag_id: null,
                number: generateUniqueEntryNumber(),
                date: expense.date,
                narration: expense.narration || `Expense: ${expenseType.name} by ${vendor ? vendor.name : user?.name ?? ''}`,
                client_id: parseInt(expense?.client_id) || parseInt(expense?.project?.client_id) || null,
                user_id: parseInt(user?.id || expense?.user_id) || null,
                project_id: parseInt(expense?.project_id) || null,

            },
            Entryitem: entryItems
        };

        // ========== STEP 10: Submit entry ==========
        try {
            const res = await axios.post(`${backendUrl}/entries/add/journal`, entryPayload, { headers });
            if (res.data?.status === 'error') {
                showToast(`❌ ${res.data.message || 'Failed to create Expense.'}`);
                return;
            }
            const webzashEntryId = res.data.entry_id;

            const backend = process.env.REACT_APP_BACKEND_URL;
            const userInfo = JSON.parse(localStorage.getItem('user-info'));
            const origin = localStorage.getItem('user-origin');
            const token = userInfo?.token;

            // Correct way to send data and headers
            await axios.post(
                `${backend}/api/expense/${expense.id}?_method=PUT`,
                { journal_id: webzashEntryId },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                        'Tenant-Origin': origin,
                    }
                }
            );
            showToast1("✅ Expense Added Successfully.");
        } catch (err) {
            showToast("❌ Failed to create Expense.");
            console.error(err);
        }
    };

    const isDateInLockedPeriod = (dateStr) => {
        if (!dateStr) return false;
        const date = new Date(dateStr);
        return lockedPeriods.some(period => {
            const start = new Date(period.start_date);
            const end = new Date(period.end_date);
            return date >= start && date <= end;
        });
    };

    const handleShowModal = (expenseId = null, mode = "view") => {
        console.log("handleShowModal called with expenseId:", expenseId, "mode:", mode);

        if (expenseId !== null) {
            const expenseToEdit = expense.find(expense => expense.id === expenseId);
            setSelectedExpenseData(expenseToEdit || null);
            setSelectedExpenseId(expenseId);
        } else {
            setSelectedExpenseData(null);
            setSelectedExpenseId(null);
            setErrors({});
        }

        // 👇 set mode here
        setIsEditMode(mode === "edit");

        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedExpenseId(null);
        setSelectedExpenseData(null);
        setErrors({});
    };

    const handlePageChange = ({ selected }) => {
        const newPage = selected + 1;
        setCurrentPage(newPage);
        fetchExpense(newPage);
    };

    // const totalExpenseAmount = expense.reduce((sum, item) => sum + Number(item.amount), 0);
    const isExceedingBudget = totalExpenseAmount > Number(projectDetails.expenditure_amount);

    return (
        <React.Fragment>
            <div>
                {/* <div className="d-flex">
                    <div className='text-muted font-size-13'>Max Expenditure Amount: {projectDetails.expenditure_amount}</div>&nbsp;&nbsp;&nbsp;
                </div> */}

                {/* Add Expense Button */}
                <WithCreatePermission>
                    <button
                        type="button"
                        className="btn rounded-circle border d-flex align-items-center justify-content-center"
                        style={{
                            width: "30px",
                            height: "30px",
                            padding: "5px",
                            backgroundColor: "ffffff",
                            borderColor: "#ccc",
                            color: "#333",
                            float: "right", marginBottom: "10px"
                        }}
                        onClick={() => handleShowModal(null, "edit")}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        id="tool"
                        title="Add Activity"
                    >
                        <i className="mdi mdi-plus" style={{ fontSize: "16px" }}></i>
                    </button>
                </WithCreatePermission>
            </div><br /><br />

            <div className="contacts-table-modern" style={{ overflow: 'visible' }}>
                <div className="table-responsive" style={{ overflow: 'visible' }}>
                    <table className="table table-modern">
                        <thead>
                            <tr>
                                <th scope="col">#</th>
                                <th scope="col">Date</th>
                                <th scope="col">Narration</th>

                                <th scope="col">User</th>
                                <th scope="col">Amount</th>

                                <th scope="col">Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {expense.length > 0 ? (
                                expense.map((expense, index) => (
                                    <tr key={expense.id}>
                                        <td>{index + 1}</td>
                                        <td onClick={() => handleShowModal(expense.id, "view")}>
                                            {expense.date}</td>
                                        <td>{expense.narration}</td>


                                        <td>
                                            {expense.user?.name || ''}
                                        </td>
                                        <td>{expense.amount}</td>

                                        <td>
                                            <div className="dropdown">
                                                <i
                                                    className="mdi mdi-dots-horizontal font-size-18"
                                                    data-bs-toggle="dropdown"
                                                    data-bs-boundary="viewport"
                                                    aria-expanded="false"
                                                    style={{ cursor: 'pointer' }}
                                                ></i>
                                                <ul className="dropdown-menu dropdown-menu-end font-size-14">
                                                    {isDateInLockedPeriod(expense.date) ? (
                                                        <li className="px-3 py-1 text-muted small d-flex align-items-center">
                                                            <i className="mdi mdi-lock me-1"></i> Locked Period
                                                        </li>
                                                    ) : (
                                                        <>
                                                            <li>
                                                                <WithEditPermission>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => handleShowModal(expense.id, "edit")}
                                                                    >
                                                                        Edit
                                                                    </button>
                                                                </WithEditPermission>
                                                            </li>
                                                            <li>
                                                                <WithDeletePermission>
                                                                    <button
                                                                        className="dropdown-item"
                                                                        onClick={() => deleteExpense(expense.id)}
                                                                    >
                                                                        Delete
                                                                    </button>
                                                                </WithDeletePermission>
                                                            </li>
                                                        </>
                                                    )}
                                                </ul>
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="8">No expense available</td>
                                </tr>
                            )}
                        </tbody>


                    </table>
                </div>
            </div>

            <div className="d-flex justify-content-end pagination-sm">
                <ReactPaginate
                    previousLabel={'<'}
                    nextLabel={'>'}
                    breakLabel={'...'}
                    pageCount={pageCount}
                    marginPagesDisplayed={2}
                    pageRangeDisplayed={3}
                    onPageChange={handlePageChange}
                    containerClassName={'pagination justify-content-center pagination-sm'}
                    pageClassName={'page-item'}
                    pageLinkClassName={'page-link'}
                    previousClassName={'page-item'}
                    previousLinkClassName={'page-link'}
                    nextClassName={'page-item'}
                    nextLinkClassName={'page-link'}
                    breakClassName={'page-item'}
                    breakLinkClassName={'page-link'}
                    activeClassName={'active'}
                />
            </div>

            {/* Render the ExpenseModal */}
            <ExpenseModal
                showModal={showModal}
                handleClose={handleCloseModal}
                expenseId={selectedExpenseId}
                expenseData={selectedExpenseData}
                addExpense={addExpense}
                editExpense={editExpense}
                errors={errors}
                setErrors={setErrors}
                fetchExpenseTypes={fetchExpenseTypes}
                expenseTypes={expenseTypes}
                fetchUsers={fetchUsers}
                users={users}
                projectId={projectId}
                clientId={projectDetails.client_id}
                isEditMode={isEditMode}
                setIsEditMode={setIsEditMode}
            />
        </React.Fragment>
    );


};
export default ExpenseList;








